import { v4 as uuidv4 } from 'uuid';
import { lineGoodsAmount, percentLineDiscount } from '@tcr-erp/shared';
import type { OrderItem, PosVariant, FulfillmentMode } from '../types';

/**
 * TCR-2224 — chuẩn MISA: Thành tiền (trước CK) = làm tròn (Đơn giá × SL) về đồng; đơn giá giữ
 * số lẻ. Dòng serial (giao ngay, không đặt trước) luôn là 1 đơn vị. Cùng helper với BE
 * (`pos-order-pricing`, `computeLineDiscount`) để hai bên ra cùng một số.
 */
export function itemGrossAmount(price: number, quantity: number, isQuantityMode: boolean): number {
  return lineGoodsAmount(price, isQuantityMode ? quantity : 1);
}

export interface PosTotals {
  totalWeight: number;
  totalGoods: number;
  totalDiscount: number;
  totalVat: number;
  /** Tổng phải trả gồm VAT (= totalGoods - totalDiscount + totalVat). */
  grandTotal: number;
}

/** Single source of truth tính tổng đơn POS (PaymentFooter + checkout cùng dùng). */
export function computePosTotals(items: OrderItem[]): PosTotals {
  let totalWeight = 0;
  let totalGoods = 0;
  let totalDiscount = 0;
  let totalVat = 0;
  let totalPayment = 0;

  for (const item of items) {
    const w = item.weight ?? 0;
    totalWeight += item.trackingMode === 'serial' ? w : w * item.quantity;
    totalGoods += lineGoodsAmount(item.price, item.quantity);
    totalDiscount += item.discountAmount;
    const lineVat = Math.round((item.totalAmount * (item.vat ?? 0)) / 100);
    totalVat += lineVat;
    // TCR-2224 — làm tròn từng dòng rồi mới cộng (quy tắc SalesInvoice).
    totalPayment += Math.round(item.totalAmount + lineVat);
  }

  return {
    totalWeight,
    totalGoods,
    totalDiscount,
    totalVat,
    grandTotal: totalPayment,
  };
}

/** Số tiền thuế VAT của một dòng (đã chiết khấu) — dùng cho hiển thị. */
export function itemVatAmount(item: OrderItem): number {
  return Math.round((item.totalAmount * (item.vat ?? 0)) / 100);
}

/** Đơn giá đã gồm VAT — TCR-1154 thống nhất hiển thị giá bao thuế. */
export function priceIncludingVat(item: OrderItem): number {
  return Math.round(item.price * (1 + (item.vat ?? 0) / 100));
}

/** Recalc discountAmount + totalAmount theo price/discountPercent/quantity hiện tại. */
export function recalcItem(item: OrderItem, fulfillmentMode: FulfillmentMode): OrderItem {
  const isQuantityMode = item.trackingMode === 'quantity' || fulfillmentMode === 'PREORDER';
  // TCR-2224 — CK % = làm tròn (Thành tiền × %CK); mọi số tiền dòng là số nguyên.
  const grossAmount = itemGrossAmount(item.price, item.quantity, isQuantityMode);
  const discountAmount = percentLineDiscount(grossAmount, item.discountPercent);
  const totalAmount = grossAmount - discountAmount;
  return { ...item, discountAmount, totalAmount };
}

/**
 * TCR-1423: mỗi lần thu ngân đổi SL ở BẤT KỲ dòng nào của một nhóm flash sale
 * (dòng flash gốc HOẶC dòng dư sinh ra từ lần tách trước), phân bổ lại suất
 * KM cho CẢ NHÓM thay vì chỉ tách một chiều.
 *
 * Bản vá trước (`splitFlashOverflow`, đã xoá) tách 1 dòng flash vượt
 * `flashRemaining` thành 2 dòng, nhưng XOÁ SẠCH mọi dấu vết flash khỏi dòng
 * dư (`flash_sale_item_id`, `flashRemaining`, ...) — QA FAILED: giảm SL ở
 * dòng flash xuống dưới hạn mức không có cách nào kéo dòng dư về lại giá KM
 * vì nó không còn field nào trỏ về chương trình. Field mới `flashOverflowOf`
 * (+ `flashOverflowRemaining`/`flashOverflowPrice`) giữ liên kết đó, để hàm
 * này luôn gom lại được cả 2 dòng thuộc cùng 1 suất flash và tính lại từ đầu:
 * `flashQty = min(tổng SL, remaining)`, phần dư (`overflowQty`) về giá gốc.
 *
 * Trả `null` khi dòng `lineId` không thuộc nhóm flash nào (không phải dòng
 * flash và không phải dòng dư) — gọi nơi dùng biết cần fallback về
 * `onUpdateItem` 1 dòng như cũ.
 */
export function rebalanceFlashGroup(
  items: OrderItem[],
  lineId: string,
  requestedQuantity: number,
  fulfillmentMode: FulfillmentMode,
  newId: () => string = uuidv4,
): OrderItem[] | null {
  const lineIdx = items.findIndex((i) => i.id === lineId);
  if (lineIdx === -1) return null;
  const line = items[lineIdx];

  // "Khoá nhóm": dòng flash tự nhận diện qua `flash_sale_item_id`, dòng dư
  // qua `flashOverflowOf` trỏ ngược lại đúng id đó.
  const groupKey = line.flash_sale_item_id ?? line.flashOverflowOf;
  if (!groupKey) return null;

  const flashIdx = items.findIndex((i) => i.flash_sale_item_id === groupKey);
  const overflowIdx = items.findIndex((i) => i.flashOverflowOf === groupKey);
  const flashLine = flashIdx !== -1 ? items[flashIdx] : undefined;
  const overflowLine = overflowIdx !== -1 ? items[overflowIdx] : undefined;

  // Dòng đang sửa (`lineId`) dùng `requestedQuantity` thay cho SL hiện tại —
  // dòng còn lại trong nhóm (nếu có) giữ nguyên SL đang lưu.
  const flashQtyBefore = flashIdx === lineIdx ? requestedQuantity : (flashLine?.quantity ?? 0);
  const overflowQtyBefore =
    overflowIdx === lineIdx ? requestedQuantity : (overflowLine?.quantity ?? 0);
  const total = flashQtyBefore + overflowQtyBefore;

  // Remaining lấy ưu tiên từ dòng flash (nguồn sống) — chỉ khi nhóm KHÔNG còn
  // dòng flash (đã bị dồn hết về dòng dư ở lần rebalance trước) mới đọc lại
  // từ snapshot `flashOverflowRemaining` trên dòng dư.
  const remaining = flashLine?.flashRemaining ?? overflowLine?.flashOverflowRemaining;
  if (typeof remaining !== 'number') return null;

  const flashQty = Math.min(total, Math.max(remaining, 0));
  const overflowQty = total - flashQty;

  const flashPrice = flashLine ? flashLine.price : (overflowLine?.flashOverflowPrice ?? 0);
  const retailPrice = flashLine
    ? (flashLine.retailPrice ?? flashLine.price)
    : (overflowLine?.price ?? 0);

  // Dòng flash: giữ/khôi phục. Nếu nhóm hiện KHÔNG còn dòng flash (trường
  // hợp đã dồn hết về dòng dư ở một lần rebalance trước, remaining sau đó
  // lại > 0), tái tạo từ dòng dư — spread `overflowLine` đã tự mang sẵn
  // flash_sale_id/flashMaxPerCustomer/flashEndAt = undefined (bị xoá lúc
  // dòng dư được tạo) nên không cần xoá tay lại.
  const nextFlashLine =
    flashQty > 0
      ? recalcItem(
          {
            ...(flashLine ?? overflowLine!),
            id: flashLine ? flashLine.id : newId(),
            quantity: flashQty,
            price: flashPrice,
            retailPrice,
            flash_sale_item_id: groupKey,
            flashRemaining: remaining,
            flashOverflowOf: undefined,
            flashOverflowRemaining: undefined,
            flashOverflowPrice: undefined,
          },
          fulfillmentMode,
        )
      : undefined;

  // Dòng dư: giữ/khôi phục, luôn về giá gốc. Nếu chưa có sẵn (tạo mới từ
  // dòng flash), phải xoá tay các field flash thật — ngược lại với nhánh
  // trên vì lần này base (`flashLine`) đang MANG chúng.
  const nextOverflowLine =
    overflowQty > 0
      ? recalcItem(
          {
            ...(overflowLine ?? flashLine!),
            id: overflowLine ? overflowLine.id : newId(),
            quantity: overflowQty,
            price: retailPrice,
            flashOverflowOf: groupKey,
            flashOverflowRemaining: remaining,
            flashOverflowPrice: flashPrice,
            flash_sale_id: undefined,
            flash_sale_item_id: undefined,
            flashRemaining: undefined,
            flashMaxPerCustomer: undefined,
            flashEndAt: undefined,
          },
          fulfillmentMode,
        )
      : undefined;

  // Giữ đúng vị trí: dòng flash luôn đứng trước dòng dư trong mảng gốc (do
  // cách dựng lúc tách lần đầu), nên vị trí nhỏ hơn trong hai (nếu tồn tại)
  // là "chỗ của cả nhóm" — chèn kết quả mới đúng vào đó, các dòng khác giữ
  // nguyên thứ tự/nội dung.
  const anchorIdx = flashIdx !== -1 ? flashIdx : overflowIdx;
  const rest = items.filter((_, i) => i !== flashIdx && i !== overflowIdx);
  const insertion = [nextFlashLine, nextOverflowLine].filter(
    (x): x is OrderItem => x !== undefined,
  );
  rest.splice(anchorIdx, 0, ...insertion);

  return rest;
}

/**
 * Ghép thông tin serial mới (đã map từ lookupVariants) vào 1 dòng đơn đang có:
 * - cập nhật serial/giá/KL/phí công/kho + snapshot gold-price
 * - GIỮ discountPercent (CK%) + sellerStaffId (NV bán) + id dòng
 * - reset warehouseStaffId nếu không còn hợp lệ trong danh sách kho mới
 */
export function applySwappedVariant(
  item: OrderItem,
  variant: PosVariant,
  fulfillmentMode: FulfillmentMode,
): OrderItem {
  const warehouseStaffId =
    item.warehouseStaffId && variant.warehouse_staff_ids?.includes(item.warehouseStaffId)
      ? item.warehouseStaffId
      : undefined;

  const merged: OrderItem = {
    ...item,
    specificationCode: variant.specification_code,
    brandId: variant.brand_id ?? item.brandId,
    price: variant.price,
    retailPrice: variant.price,
    weight: variant.weight,
    laborFee: variant.labor_fee,
    vat: variant.vat,
    vatOverride: undefined,
    // TCR-1364: đổi serial = biến thể khác, VAT sửa tay của biến thể CŨ không còn
    // ý nghĩa cho biến thể MỚI — reset luôn cờ để lần suggest B2B tiếp theo (nếu
    // có) được phép tự set VAT theo bảng giá, không bị coi là "đã sửa tay".
    hasVatOverride: undefined,
    warehouseStaffIds: variant.warehouse_staff_ids,
    warehouseStaffId,
  };
  return recalcItem(merged, fulfillmentMode);
}

/**
 * Dòng hàng mới từ một biến thể vừa tra được.
 *
 * `newId` tiêm từ ngoài để test không phụ thuộc uuid ngẫu nhiên — cùng quy ước
 * với `IdFactory` trong `applyCombo.ts`.
 */
export function variantToOrderItem(
  variant: PosVariant,
  fulfillmentMode: FulfillmentMode,
  newId: () => string = uuidv4,
): OrderItem {
  // Giá flash áp NGAY tại đây, không đợi `/pos/promotions/evaluate`.
  //
  // Hai lý do: (1) thu ngân thấy đúng giá ngay khoảnh khắc dòng hàng sinh ra,
  // không nhìn số nhảy sau 500ms debounce; (2) engine tra tường minh bằng
  // `flash_sale_item_id` và MONG ĐỢI `unit_price` đã là giá flash (TCR-1143) —
  // gửi giá niêm yết sẽ làm nó trừ giảm giá thêm một lần nữa.
  const flash = variant.flash_sale;
  const price = flash ? flash.flash_price : variant.price;
  const quantity = 1;
  return {
    id: newId(),
    variantId: variant.variant_id,
    productId: variant.product_id,
    brandId: variant.brand_id,
    // Fix round 2 (F5): SỐ NHIỀU — `mapDetailToPosVariant` là nguồn duy nhất
    // dựng `variant.category_ids` và đã chuẩn hoá mảng rỗng/thiếu ⇒
    // `undefined`, chuyển thẳng không cần đoán lại ở đây.
    categoryIds: variant.category_ids,
    variantName: variant.variant_name,
    variantDisplayName: variant.variant_display_name,
    variantCode: variant.variant_code,
    specificationCode: variant.specification_code,
    trackingMode: variant.tracking_mode,
    price,
    // Giá gốc để hiện gạch ngang. KHÔNG set `hasPriceOverride` và giữ
    // `discountPercent/discountAmount = 0`: giá flash là giá của CHƯƠNG TRÌNH,
    // không phải thu ngân sửa tay — đánh dấu nhầm là bắt thu ngân nhập lý do
    // sửa giá và đẩy đơn qua cổng quyền `pos.discount`.
    retailPrice: flash ? flash.original_price : price,
    productImage: variant.image,
    discountPercent: 0,
    discountAmount: 0,
    discountType: 'percent',
    quantity,
    weight: variant.weight,
    laborFee: variant.labor_fee,
    vat: variant.vat,
    vatOverride: undefined,
    // TCR-1364: dòng hàng mới tinh — chưa ai sửa VAT tay, để cờ trống cho
    // suggest B2B (nếu KH là tổ chức) được tự set VAT theo bảng giá đặc biệt.
    hasVatOverride: undefined,
    totalAmount: itemGrossAmount(
      price,
      quantity,
      variant.tracking_mode === 'quantity' || fulfillmentMode === 'PREORDER',
    ),
    warehouseStaffIds: variant.warehouse_staff_ids,
    flash_sale_item_id: flash?.item_id,
    flashRemaining: flash?.remaining,
    flashMaxPerCustomer: flash?.max_per_customer,
    flashEndAt: flash?.end_at,
    // TCR-1517 — COMBO THẬT: mang định mức sang dòng giỏ để thu ngân bung ra
    // xem bên trong có gì NGAY LÚC ĐANG BÁN (panel ở `POS/components/
    // OrderItemsTable.tsx`). Chỉ hiển thị — không đổi giá, không sinh dòng.
    //
    // ⚠️ HÀM NÀY CÓ HAI BẢN. Bản kia ở `pages/Orders/edit.tsx` (màn sửa đơn tự
    // giữ một bản riêng). Sửa thiếu một bản = một trong hai màn IM LẶNG không
    // có thành phần — đúng lớp lỗi đã làm mất ảnh sản phẩm ở màn sửa đơn
    // (xem chú thích `productImage` bên đó). Đổi ở đây thì đổi cả bên kia.
    is_combo_product: variant.is_combo_product,
    bom_components: variant.bom_components,
  };
}
